import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/ServicosCategorias_CarregaCategoriasInput.ts';
import '../contracts/ServicosCategorias_CreateInput.ts';
import '../contracts/ServicosCategorias_UpdateInput.ts';
import '../contracts/ServicosCategorias_DeleteInput.ts';

import '../contracts/ServicosCategorias_CarregaCategoriasResponse';
import '../contracts/ServicosCategorias_CreateResponse';
import '../contracts/ServicosCategorias_DeleteResponse';
import '../contracts/ServicosCategorias_UpdateResponse';
import type { ServicosCategorias_CreateInput } from '../contracts/ServicosCategorias_CreateInput';
import type { ServicosCategorias_DeleteInput } from '../contracts/ServicosCategorias_DeleteInput';
import type { ServicosCategorias_UpdateInput } from '../contracts/ServicosCategorias_UpdateInput';
/**
 * Composable for managing service categories (ServicosCategorias)
 *
 * Provides methods for CRUD operations on service categories
 */
export function useServicosCategorias() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all service categories
   * @returns {Promise<Array>} Array of category objects
   */
  const fetchCategorias = async () => {
    log('useServicosCategorias', 'fetchCategorias', 'Fetching service categories');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/servicos_categorias/carrega_categorias', null, { inputContract: 'ServicosCategorias_CarregaCategoriasInput', responseContract: 'ServicosCategorias_CarregaCategoriasResponse' });

      if (response.success) {
        log('useServicosCategorias', 'fetchCategorias', 'Categories loaded successfully', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao carregar categorias');
      }
    } catch (err) {
      log('useServicosCategorias', 'fetchCategorias', 'Error fetching categories', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new service category
   * @param {Object} categoriaData - Category data
   * @param {string} categoriaData.servicoscategoria_nome - Category name
   * @param {number} categoriaData.servicoscategoria_prazo_retorno_dias - Return deadline in days
   * @param {number} categoriaData.servicoscategoria_prazo_garantia_dias - Warranty deadline in days
   * @param {number} categoriaData.servicoscategoria_produtos_associados - Associated products (0/1)
   * @returns {Promise<Object>} Created category with ID
   */
  const createCategoria = async (categoriaData: ServicosCategorias_CreateInput) => {
    log('useServicosCategorias', 'createCategoria', 'Creating category', categoriaData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('ServicosCategorias_CreateInput', categoriaData);
      const response = await httpService.post('/index.php/config/servicos_categorias/create', categoriaData, { responseContract: 'ServicosCategorias_CreateResponse' });

      if (response.success) {
        log('useServicosCategorias', 'createCategoria', 'Category created successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar categoria');
      }
    } catch (err) {
      log('useServicosCategorias', 'createCategoria', 'Error creating category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update an existing service category
   * @param {number} id - Category ID
   * @param {Object} categoriaData - Category data to update
   * @returns {Promise<Object>} Response object
   */
  const updateCategoria = async (id: any, categoriaData: any) => {
    log('useServicosCategorias', 'updateCategoria', 'Updating category', { id, categoriaData });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: ServicosCategorias_UpdateInput = {
        servicoscategoria_id: id,
        ...categoriaData
      };

      validate('ServicosCategorias_UpdateInput', payload);
      const response = await httpService.post('/index.php/config/servicos_categorias/update', payload, { responseContract: 'ServicosCategorias_UpdateResponse' });

      if (response.success) {
        log('useServicosCategorias', 'updateCategoria', 'Category updated successfully');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar categoria');
      }
    } catch (err) {
      log('useServicosCategorias', 'updateCategoria', 'Error updating category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a service category
   * @param {number} id - Category ID
   * @returns {Promise<Object>} Response object
   */
  const deleteCategoria = async (id: any) => {
    log('useServicosCategorias', 'deleteCategoria', 'Deleting category', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: ServicosCategorias_DeleteInput = {
        servicoscategoria_id: id
      };
      validate('ServicosCategorias_DeleteInput', payload);
      const response = await httpService.post('/index.php/config/servicos_categorias/delete', payload, { responseContract: 'ServicosCategorias_DeleteResponse' });

      if (response.success) {
        log('useServicosCategorias', 'deleteCategoria', 'Category deleted successfully');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao excluir categoria');
      }
    } catch (err) {
      log('useServicosCategorias', 'deleteCategoria', 'Error deleting category', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria
  };
}
